import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

/** Identify Other options to display text input */
const OTHER = 'Other';

/** Three tiered reason options. Primary & secondary issue description options shown based on prior selection  */
const IssueSourceOptions: {
    name: string;
    primaryIssueDescriptionOptions: {
        name: string;
        secondaryIssueDescriptionOptions?: string[];
    }[];
}[] = [
    {
        name: 'Client',
        primaryIssueDescriptionOptions: [
            {
                name: 'Authorization not sent to FMH',
                secondaryIssueDescriptionOptions: [
                    'API issue',
                    'Approved outside of system',
                    'Coding issue',
                ],
            },
            {
                name: 'Coding issue',
                secondaryIssueDescriptionOptions: [
                    'Member coverage coded incorrectly',
                    'Pharmacy PBM coding',
                ],
            },
            {
                name: 'Information request',
                secondaryIssueDescriptionOptions: [
                    'Brand/Generic confirmation',
                    'Member information',
                    'Prescriber information',
                ],
            },
            {
                name: 'Other authorization issue',
                secondaryIssueDescriptionOptions: [
                    'Missing authorization information',
                    'PA entered incorrectly',
                    'PA not approved by client',
                    'PA not entered at PBM',
                ],
            },
            {
                name: 'Quantity limit issue',
                secondaryIssueDescriptionOptions: [
                    'PLA entered incorrectly',
                    'PLA not approved by client',
                    'PLA not entered at PBM',
                ],
            },
            { name: 'Requester confusion' },
            { name: OTHER },
        ],
    },
    {
        name: 'Free Market Health',
        primaryIssueDescriptionOptions: [
            { name: 'Network management issue' },
            {
                name: 'System failure',
                secondaryIssueDescriptionOptions: [
                    'Authorization ingestion issue',
                    'Pharmacy lock-in issue',
                    'Referral assignment error',
                    'Other system error',
                ],
            },
            {
                name: 'Operational issue',
                secondaryIssueDescriptionOptions: [
                    'Incorrect pharmacy assignment',
                    'Program configuration error',
                ],
            },
            { name: OTHER },
        ],
    },
    {
        name: 'Patient',
        primaryIssueDescriptionOptions: [
            {
                name: 'Financial issue',
                secondaryIssueDescriptionOptions: ['Co-pay issue', 'Pharmacy balance issue'],
            },
            {
                name: 'Member education needed',
                secondaryIssueDescriptionOptions: ['Assignment inquiry', 'Patient refusal'],
            },
            {
                name: 'Override request',
                secondaryIssueDescriptionOptions: [
                    'Loss/damaged medication',
                    'Out of network pharmacy fill',
                    'Vacation',
                ],
            },
            {
                name: 'Reassignment request',
                secondaryIssueDescriptionOptions: [
                    'Member preference approved',
                    'Member preference denied',
                    'Patient address change',
                ],
            },
            { name: 'Unresponsive to outreach' },
            { name: OTHER },
        ],
    },
    {
        name: 'Pharmacy',
        primaryIssueDescriptionOptions: [
            { name: 'Billing issue' },
            { name: 'Financial issue' },
            {
                name: 'Failure to notify FMH',
                secondaryIssueDescriptionOptions: [
                    'Medication access change',
                    'Medication stock change',
                    'Prescriber list change',
                    'State access change',
                    'Unable to Service Secondary',
                ],
            },
            {
                name: 'No member outreach',
                secondaryIssueDescriptionOptions: [
                    'No attempts made',
                    'Incorrect contact information',
                ],
            },
            {
                name: 'Prescription issue',
                secondaryIssueDescriptionOptions: ['Mishandling of script', 'No attempts made'],
            },
            {
                name: 'Servicing member issue',
                secondaryIssueDescriptionOptions: [
                    'No notification to FMH of issue',
                    'Poor pharmacy service',
                    'Shipment delays',
                ],
            },
            { name: 'State access issue' },
            { name: 'Unresponsive to outreach' },
            { name: OTHER },
        ],
    },
    {
        name: 'Prescriber',
        primaryIssueDescriptionOptions: [
            {
                name: 'Prescription issue',
                secondaryIssueDescriptionOptions: [
                    'Incorrect script information',
                    'Prescriber refusal',
                ],
            },
            { name: 'Unresponsive to outreach' },
            { name: OTHER },
        ],
    },
    {
        name: 'Other',
        primaryIssueDescriptionOptions: [
            { name: 'Expedite request' },
            { name: 'Medication access inquiry' },
            { name: 'Member unmanage request' },
            { name: 'Out of scope' },
            { name: 'Rate review' },
            { name: 'Status inquiry' },
            { name: OTHER },
        ],
    },
];

const ValidationSchema = Yup.object({
    issueSource: Yup.object({
        name: Yup.string().required(validationStrings.requiredField.errorMessage),
        primaryIssueDescriptionOptions: Yup.array(
            Yup.object({
                name: Yup.string().required(),
                secondaryIssueDescriptionOptions: Yup.array(Yup.string().required()),
            }).required(),
        ).required(),
    }).required(),
    primaryIssueDescription: Yup.object({
        name: Yup.string().required(validationStrings.requiredField.errorMessage),
        secondaryIssueDescriptionOptions: Yup.array(Yup.string().required()),
    }).required(),
    secondaryIssueDescription: Yup.string().when('primaryIssueDescription', {
        is: (
            primaryIssueDescription: (typeof IssueSourceOptions)[0]['primaryIssueDescriptionOptions'][0],
        ) =>
            primaryIssueDescription?.name === OTHER ||
            (primaryIssueDescription?.secondaryIssueDescriptionOptions ?? []).length > 0,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
});

export const ResolveReasonInputs = () => {
    const formik = useFormikContext<Yup.InferType<typeof ValidationSchema>>();
    return (
        <>
            <SuggestSingleSelectFormik
                id="issueSource.name"
                label="Issue Source"
                required
                values={IssueSourceOptions}
                onChange={(selectedItem) => {
                    (async () => {
                        await formik.setFieldValue('issueSource', selectedItem);
                        await formik.setFieldValue('primaryIssueDescription', undefined);
                        await formik.setFieldValue('secondaryIssueDescription', undefined);
                    })();
                }}
            />
            <SuggestSingleSelectFormik
                id="primaryIssueDescription.name"
                label="Primary Issue Description"
                required
                disabled={!formik.values.issueSource}
                values={formik.values.issueSource?.primaryIssueDescriptionOptions}
                onChange={(selectedItem) => {
                    (async () => {
                        await formik.setFieldValue('primaryIssueDescription', selectedItem);
                        await formik.setFieldValue('secondaryIssueDescription', undefined);
                    })();
                }}
            />
            {(() => {
                if (formik.values.primaryIssueDescription?.name === OTHER) {
                    return (
                        <TextInputFormik
                            id="secondaryIssueDescription"
                            label="Secondary Issue Description"
                            required
                        />
                    );
                }
                return formik.values.primaryIssueDescription?.secondaryIssueDescriptionOptions ? (
                    <SuggestSingleSelectFormik
                        id="secondaryIssueDescription"
                        label="Secondary Issue Description"
                        required
                        values={
                            formik.values.primaryIssueDescription.secondaryIssueDescriptionOptions
                        }
                    />
                ) : null;
            })()}
        </>
    );
};

ResolveReasonInputs.InitialValues = {} as Yup.InferType<typeof ValidationSchema>;
ResolveReasonInputs.ValidationSchema = ValidationSchema;
ResolveReasonInputs.submitValues = (values: Yup.InferType<typeof ValidationSchema>) => ({
    issueSource: values.issueSource.name,
    primaryIssueDescription: values.primaryIssueDescription.name,
    secondaryIssueDescription: values.secondaryIssueDescription,
});
